/**
 * Hook for checking which transactions have history
 * Extracted from Activity.tsx to improve code organization
 */

import { useState, useEffect, useRef } from "react";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import { TransactionWithDate } from "../../../utils/transactionProcessing";

export interface UseTransactionHistoryCheckResult {
  transactionHasHistory: Set<string>;
  setTransactionHasHistory: React.Dispatch<React.SetStateAction<Set<string>>>;
  /** True when the initial history check for the current transactions has finished (so edited icons can show with the list) */
  historyCheckComplete: boolean;
}

// Cache per workspace so re-entering Activity (e.g. from Analytics) doesn't re-run 25 history reads
const historyCheckCache = new Map<string, { ids: string[]; hasHistory: string[]; timestamp: number }>();
const HISTORY_CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook for checking which transactions have history (for filtering and icon display)
 */
export const useTransactionHistoryCheck = (
  workspaceId: string,
  transactions: TransactionWithDate[]
): UseTransactionHistoryCheckResult => {
  const transactionIds = transactions.map((t) => t.id).sort();
  const cacheKey = `${workspaceId}:${transactionIds.slice(0, 25).join(",")}`;
  const cached = historyCheckCache.get(cacheKey);
  const hasValidHistoryCache = !!(
    cached &&
    Date.now() - cached.timestamp < HISTORY_CACHE_EXPIRY_MS &&
    cached.ids.length === transactionIds.slice(0, 25).length &&
    cached.ids.every((id, i) => id === transactionIds[i])
  );

  const [transactionHasHistory, setTransactionHasHistory] = useState<Set<string>>(() => {
    if (hasValidHistoryCache && cached) return new Set(cached.hasHistory);
    return new Set();
  });
  const [historyCheckComplete, setHistoryCheckComplete] = useState<boolean>(hasValidHistoryCache);
  const checkedTransactionIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!workspaceId || transactions.length === 0) {
      setTransactionHasHistory(new Set());
      checkedTransactionIdsRef.current = new Set();
      setHistoryCheckComplete(true);
      return;
    }

    if (hasValidHistoryCache && cached) {
      checkedTransactionIdsRef.current = new Set(transactionIds);
      return;
    }

    let isCancelled = false;
    setHistoryCheckComplete(false);

    const checkHistory = async (): Promise<void> => {
      const hasHistorySet = new Set<string>();
      
      const transactionsToCheck = transactions.filter((t) => !checkedTransactionIdsRef.current.has(t.id));
      
      if (transactionsToCheck.length === 0) {
        if (!isCancelled) setHistoryCheckComplete(true);
        return;
      }

      await Promise.all(
        transactionsToCheck.map(async (transaction) => {
          if (isCancelled) return;
          
          try {
            const historyRef = collection(db, "workspaces", workspaceId, "transactions", transaction.id, "history");
            const q = query(historyRef, orderBy("timestamp", "desc"));
            const snapshot = await getDocs(q);
            
            // Helper function to check if changes are only attachment-related
            const isOnlyAttachmentChanges = (changesArray: string[]): boolean => {
              return changesArray.every((change: string) => 
                change.startsWith("Attachment added") || 
                change.startsWith("Attachment removed") || 
                change.startsWith("Attachment replaced")
              );
            };
            
            // Check all history entries to see if any have non-attachment changes
            let hasNonAttachmentChanges = false;
            for (const doc of snapshot.docs) {
              const data = doc.data();
              const changes = data.changes || [];
              // Skip "Created" entry
              if (changes.length === 1 && changes[0] === "Created") {
                continue;
              }
              // If this entry has non-attachment changes, mark as edited
              if (!isOnlyAttachmentChanges(changes)) {
                hasNonAttachmentChanges = true;
                break;
              }
            }
            
            // Only mark as having history if there are non-attachment changes
            if (hasNonAttachmentChanges) {
              hasHistorySet.add(transaction.id);
            }
          } catch (error) {
            // Ignore errors for transactions without history subcollection
          }
        })
      );
      
      if (!isCancelled) {
        transactionsToCheck.forEach((t) => checkedTransactionIdsRef.current.add(t.id));
        setTransactionHasHistory((prev) => {
          const merged = new Set(prev);
          hasHistorySet.forEach((id) => merged.add(id));
          historyCheckCache.set(cacheKey, {
            ids: transactionIds.slice(0, 25),
            hasHistory: Array.from(merged),
            timestamp: Date.now(),
          });
          return merged;
        });
        setHistoryCheckComplete(true);
      }
    };

    checkHistory();
    
    return () => {
      isCancelled = true;
    };
  }, [workspaceId, transactions.length]); // Only depend on length, not the full array or transactionHasHistory (would cause infinite loop)

  return {
    transactionHasHistory,
    setTransactionHasHistory,
    historyCheckComplete,
  };
};
